'use client'

import React, { useEffect, useState } from 'react'
import ErrorMessage from './error.message'
import InputWrapper, {
  Input as InputStyle,
  Label,
  TextArea,
  TextAreaWrapper,
  LockOutlinedWrapper,
} from './input.label.style'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import { IInputWithLabelProps, ITextAreaProps } from './inputInterface'
import { formErrorMessage } from '@/constants'
import { LabelInfo } from '@/components/v1/ui/inputTextArea/inputTextArea.style'

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

const Input = ({
  label = 'Your label',
  labelInfo = '',
  addonBefore = '',
  addonAfter = undefined,
  note = undefined,
  name,
  type,
  validation,
  onChange,
  value = '',
  placeholder = '',
  disable,
  containerStyle,
  onKeyPress,
  isErrorArray,
  isErrorArrayTouch,
  customError,
  inputNumber,
  breakLineBottom,
  maxLength,
  showCount,
  valueStyle,
  onBlur,
  errorText,
}: IInputWithLabelProps) => {
  const isError =
    (validation?.errors[name] || isErrorArray) &&
    (validation?.touched[name] || isErrorArrayTouch)
  const [inputValue, setInputValue] = useState(value)

  const onValueChange = (event: any) => {
    let { value: valueText } = event.target
    if (inputNumber) {
      valueText = valueText.replace(new RegExp(/[^\d]/, 'ig'), '')
    }

    setInputValue(valueText)
    if (validation) validation?.setFieldValue(name, valueText)
    if (onChange) onChange(valueText)
  }

  useEffect(() => {
    setInputValue(value)
    if (validation) validation?.setFieldValue(name, value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <InputWrapper style={{ ...containerStyle }}>
      <Label flex>
        {label}
        {labelInfo && <LabelInfo>{labelInfo}</LabelInfo>}
      </Label>
      <InputStyle
        style={valueStyle && valueStyle}
        addonBefore={addonBefore}
        addonAfter={addonAfter}
        placeholder={placeholder}
        status={isError || customError ? 'error' : ''}
        name={name}
        type={type}
        onChange={onValueChange}
        value={inputValue}
        disabled={disable ? true : false}
        suffix={
          disable ? (
            //@ts-ignore Antdesign types incompatible with react@19
            <LockOutlinedWrapper />
          ) : (
            ''
          )
        }
        onKeyDown={onKeyPress}
        maxLength={maxLength}
        showCount={showCount}
        onBlur={() => onBlur && onBlur(inputValue)}
      />
      {isError && (
        <ErrorMessage
          breakLineBottom={breakLineBottom}
          label={validation?.errors[name] || isErrorArray}
        />
      )}
      {customError ||
        (errorText && (
          <ErrorMessage
            label={
              errorText || formErrorMessage[customError!] || '' || isErrorArray
            }
          />
        ))}
      <Label note={!!note}>{note}</Label>
    </InputWrapper>
  )
}

export const TextAreaInput = ({
  label = 'Title',
  placeholder,
  value,
  onChange,
  containerStyle,
  validation,
  name,
  note = undefined,
  disabled,
}: ITextAreaProps) => {
  const [inputValue, setInputValue] = useState(value)
  const onValueChange = (value: any) => {
    if (onChange) onChange(value.target.value)
    setInputValue(value.target.value)
    if (validation) validation?.setFieldValue(name, value.target.value)
  }

  useEffect(() => {
    setInputValue(value || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <TextAreaWrapper>
      <Label>{label}</Label>
      <TextArea
        disabled={disabled}
        placeholder={placeholder}
        value={inputValue}
        onChange={onValueChange}
        style={{ ...containerStyle }}
        rows={5}
      />
      {note && <Label note={!!note}>{note}</Label>}
    </TextAreaWrapper>
  )
}

export default Input
